import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Support from "@/pages/Support";
import RefundPolicy from "@/pages/RefundPolicy";
import EULA from "@/pages/EULA";
import DashboardLayout from "@/pages/DashboardLayout";
import DashboardHome from "@/pages/DashboardHome";
import Leaderboard from "@/pages/Leaderboard";
import Itinerary from "@/pages/Itinerary";
import Sales from "@/pages/Sales";
import Employees from "@/pages/Employees";
import ActivityLog from "@/pages/ActivityLog";
import Chat from "@/pages/Chat";
import Integrations from "@/pages/Integrations";
import CompanySettings from "@/pages/CompanySettings";
import SalesReview from "@/pages/SalesReview";
import TeamsArena from "@/pages/TeamsArena";
import Training from "@/pages/Training";
import TrainingModuleView from "@/pages/TrainingModuleView";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import PlatformAdmin from "@/pages/PlatformAdmin";
import Onboarding from "@/pages/Onboarding";
import PortalHome from "@/pages/PortalHome";
import PremiumSupport from "@/pages/PremiumSupport";
import { PORTAL_HOME } from "@/lib/webAccess";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && user.needs_onboarding) return <Navigate to="/onboarding" replace />;
  if (user && user.role === "platform_admin") return <Navigate to="/admin" replace />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function DefaultLandingRedirect() {
  const [route, setRoute] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/companies/me")
      .then((res) => { if (alive) setRoute(res.data?.default_landing_route || "/dashboard/chat"); })
      .catch(() => { if (alive) setRoute("/dashboard/chat"); });
    return () => { alive = false; };
  }, []);
  if (!route) return null;
  return <Navigate to={route} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/support" element={<Support />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/eula" element={<EULA />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute roles={["owner", "manager", "employee"]}>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {/* Web is now a lightweight Account Portal — the full game experience
                  lives in the native mobile apps. The dashboard game routes below are
                  intentionally gated to the portal home; their page components remain
                  in the codebase and can be re-enabled at any time (see lib/webAccess.js). */}
              <Route index element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="home" element={<PortalHome />} />
              <Route path="overview" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="leaderboard" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="itinerary" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="sales" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="chat" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="profile" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="import-sales" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="support" element={<PremiumSupport />} />
              <Route path="sales-review" element={<SalesReview />} />
              <Route path="teams-arena" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="training" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="training/:moduleId" element={<Navigate to={PORTAL_HOME} replace />} />
              {/* Legacy routes */}
              <Route path="arena-preview" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="chat-preview" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="challenges" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="badges" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="employees" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="activity" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="integrations" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route path="settings" element={<Navigate to={PORTAL_HOME} replace />} />
              <Route
                path="billing"
                element={
                  <ProtectedRoute roles={["owner", "manager"]}>
                    <Billing />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route
              path="/billing/success"
              element={
                <ProtectedRoute roles={["owner", "admin"]}>
                  <BillingSuccess />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={["platform_admin"]}>
                  <PlatformAdmin />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
